import React from "react";
import {
	CheckCircleFilled,
	ClockCircleOutlined,
	ExclamationCircleFilled,
	LoadingOutlined,
	MinusCircleOutlined,
	SyncOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import {
	FlowEdge,
	FlowNode,
	StageLogStatus,
} from "../../../../../../types/healthcare/TreatmentPlanTypes";

dayjs.extend(relativeTime);

export type DotStatus = StageLogStatus | "pending" | "current";

// Stage icons are defined once in the shared source of truth; re-exported here so existing
// imports (DrawerStageList, PeptideDashboardPage) keep working unchanged.
export { getStageIcon } from "../../../../../../utils/healthcare/treatmentPlanIcons";

export const getStatusDot = (status: DotStatus, size = 16) => {
	const s = { fontSize: size };
	switch (status) {
		case "completed":
			return <CheckCircleFilled style={{ ...s, color: "var(--mg-success)" }} />;
		case "failed":
			return <ExclamationCircleFilled style={{ ...s, color: "var(--mg-danger)" }} />;
		case "waiting":
			return <LoadingOutlined style={{ ...s, color: "var(--mg-warning)" }} />;
		case "in_progress":
			return <SyncOutlined spin style={{ ...s, color: "var(--mg-primary)" }} />;
		case "current":
			return <SyncOutlined spin style={{ ...s, color: "var(--mg-primary)" }} />;
		case "skipped":
			return <MinusCircleOutlined style={{ ...s, color: "var(--mg-text-disabled)" }} />;
		default:
			return <ClockCircleOutlined style={{ ...s, color: "var(--mg-border)" }} />;
	}
};

export const topoSort = (nodes: FlowNode[], edges: FlowEdge[]): FlowNode[] => {
	const adj = new Map<string, string[]>();
	const deg = new Map<string, number>();
	nodes.forEach((n) => {
		adj.set(n.id, []);
		deg.set(n.id, 0);
	});
	edges.forEach((e) => {
		adj.get(e.source)?.push(e.target);
		deg.set(e.target, (deg.get(e.target) ?? 0) + 1);
	});
	const queue = nodes.filter((n) => deg.get(n.id) === 0);
	const out: FlowNode[] = [];
	while (queue.length) {
		const node = queue.shift()!;
		out.push(node);
		adj.get(node.id)?.forEach((t) => {
			const d = (deg.get(t) ?? 1) - 1;
			deg.set(t, d);
			if (d === 0) {
				const tn = nodes.find((n) => n.id === t);
				if (tn) queue.push(tn);
			}
		});
	}
	nodes.forEach((n) => {
		if (!out.find((r) => r.id === n.id)) out.push(n);
	});
	return out;
};

export const fmtDuration = (from?: string | null, to?: string | null) => {
	if (!from) return "—";
	const mins = dayjs(to ?? undefined).diff(dayjs(from), "minute");
	if (mins < 60) return `${mins}m`;
	const hrs = Math.floor(mins / 60);
	if (hrs < 24) return `${hrs}h ${mins % 60}m`;
	return `${Math.floor(hrs / 24)}d ${hrs % 24}h`;
};
